/**
 * The Rocket Pass: seventy tiers, two tracks, and Pro Tiers after that.
 *
 * ===========================================================================
 * THE ECONOMY, AND WHERE EVERY TIER POINT COMES FROM
 * ===========================================================================
 *
 * A tier costs `TIER_POINTS_PER_TIER` (10) Tier Points and there are exactly
 * two sources, both fixed by the contract in ./cosmetics:
 *
 *   an XP level      `TIER_POINTS_PER_LEVEL` = 10  -> one tier per level
 *   a challenge      `CHALLENGE_TIER_POINTS`  = 3 / 5 / 8 by difficulty
 *
 * That is the whole of it, and nothing here mints a Tier Point from anything
 * else. The conversion itself is `applyTierPoints` in ./profile — one writer
 * for `passTier`, so "tiers advance at exactly ten" is a property of a single
 * function rather than of every call site.
 *
 * The XP curve is ./profile's too (`levelForXp`). This file only ever asks it
 * what level a total corresponds to; two files deciding when you level up is
 * how a player gains a level and no tier.
 *
 * ===========================================================================
 * PREMIUM IS BOUGHT WITH EARNED CREDITS, AND ONLY THOSE
 * ===========================================================================
 *
 * `unlockPremium` spends `PASS_PREMIUM_COST` from the wallet. There is no
 * payment path in this codebase, no currency purchase, and none may be added —
 * see the header of ./cosmetics. The premium track is designed to pay itself
 * back: the credit rewards on it total `PREMIUM_CREDIT_RETURN`, which is above
 * the unlock cost, exactly as the real pass is.
 *
 * ===========================================================================
 * WHERE "CLAIMED" LIVES
 * ===========================================================================
 *
 * `PlayerProgress` carries `passTier` and `passPremium` but has no field for
 * which tiers have been CLAIMED, and ./cosmetics is a contract this file does
 * not get to edit. So the claim ledger is a sidecar — `PassClaims` below,
 * versioned and persisted under its own key, in the same spirit as the contract
 * versioning the cosmetics profile separately from `GameSettings`. Every
 * function takes it explicitly, so the rules stay pure and the storage is one
 * small pair of functions at the bottom.
 */

import { Rng } from '../physics/rng';
import { legacyKeyFor, readMigrating } from './storage-migration';
import { grantItem } from './inventory';
import { ITEMS } from './items';
import { applyTierPoints, browserStorage, levelForXp, type ProfileStorage } from './profile';
import {
  CHALLENGE_TIER_POINTS,
  PAINT_COLORS,
  PASS_PREMIUM_COST,
  PASS_TIERS,
  TIER_POINTS_PER_LEVEL,
  TIER_POINTS_PER_TIER,
  type ChallengeDifficulty,
  type ItemDef,
  type PaintColor,
  type PlayerProfile,
  type PlayerProgress,
  type Rarity,
} from './cosmetics';

// ---------------------------------------------------------------------------
// Rewards
// ---------------------------------------------------------------------------

/**
 * What a tier gives.
 *
 * Deliberately NOT `ChallengeReward`: a Pro Tier grants a PAINTED variant and
 * the challenge reward union has nowhere to put the colour. Sharing the type
 * and smuggling the paint through the item id would make the paint invisible to
 * the renderer, which reads it off the inventory entry.
 */
export type PassReward =
  | { kind: 'credits'; amount: number }
  | { kind: 'xp'; amount: number }
  | { kind: 'item'; itemId: string; paint?: PaintColor };

export type PassTrack = 'free' | 'premium';

/** Both tracks' rewards for one tier. The free one is null on four in five. */
export interface TierRewards {
  free: PassReward | null;
  premium: PassReward | null;
}

/** Credits handed back by the premium track over the seventy base tiers. */
export const PREMIUM_CREDIT_RETURN = 1050;

/** Credits paid at each tier divisible by ten. 7 x 150 = the return above. */
const PREMIUM_CREDIT_STEP = 150;

/** XP handed out by the free track's odd fifth-tier steps. */
const FREE_XP_STEP = 500;

/** Credits on the free track's twenty-fifth and fiftieth tiers. */
const FREE_CREDIT_STEP = 200;

/**
 * Rarity a pass item is drawn at, by depth. The pass gets better as it goes,
 * which is the shape of the real one and the only reason to keep going once
 * the credits have paid themselves back.
 */
const premiumRarityFor = (tier: number): Rarity =>
  tier > PASS_TIERS || tier >= 50 ? 'exotic' : tier >= 30 ? 'import' : 'veryRare';

const freeRarityFor = (tier: number): Rarity => (tier >= 40 ? 'import' : 'rare');

/**
 * Pick one catalogue item for a tier, deterministically.
 *
 * Pass items should be pass items: the pool prefers entries flagged
 * `exclusive`, which is what the contract means by "not obtainable from drops;
 * event or pass only". It falls back through the requested rarity to the whole
 * catalogue rather than returning nothing, because a tier with no reward is a
 * row the player can never clear.
 */
const pickItem = (
  catalogue: readonly ItemDef[],
  rarity: Rarity,
  tier: number,
  salt: number
): ItemDef | null => {
  const exclusive = catalogue.filter(item => item.exclusive && item.rarity === rarity);
  const sameRarity = catalogue.filter(item => item.rarity === rarity);
  const pool = exclusive.length > 0 ? exclusive : sameRarity.length > 0 ? sameRarity : catalogue;
  if (pool.length === 0) return null;
  const rng = new Rng((Math.imul(tier + salt, 0x85eb_ca6b) ^ 0x27d4_eb2f) >>> 0);
  return pool[rng.nextInt(pool.length)];
};

/**
 * The step past seventy this tier is, or null for a base tier.
 *
 * Named rather than open-coded because the reward table, the row label and the
 * paint roll all need the same answer, and `tier - PASS_TIERS` computed in
 * three places is three chances to be off by one.
 */
export const proTierStep = (tier: number): number | null =>
  tier > PASS_TIERS ? tier - PASS_TIERS : null;

/**
 * The paint on a Pro Tier reward.
 *
 * Titanium White on every tenth Pro Tier and nowhere else, because the contract
 * is explicit that it is the one everybody wants and should be the rarest roll
 * rather than another colour in the list.
 */
export const proTierPaint = (tier: number): PaintColor => {
  const step = tier - PASS_TIERS;
  if (step % 10 === 0) return PAINT_COLORS[PAINT_COLORS.length - 1];
  const rng = new Rng((Math.imul(step, 0xc2b2_ae35) ^ 0x1b87_3593) >>> 0);
  return PAINT_COLORS[rng.nextInt(PAINT_COLORS.length - 1)];
};

/**
 * What tier `tier` gives on each track.
 *
 * FREE: something every fifth tier and nothing in between, which is the free
 * track's whole character — it is a trickle, and the premium row beside it
 * being full on every line is what the unlock is buying.
 *
 * PREMIUM: something on every tier; credits on every tenth; a painted item on
 * every fifth. Past seventy every Pro Tier is a painted variant, as the real
 * game does it, and there is no free counterpart at all.
 */
export const tierRewards = (
  tier: number,
  catalogue: readonly ItemDef[] = ITEMS
): TierRewards => {
  if (tier < 1) return { free: null, premium: null };

  if (proTierStep(tier) !== null) {
    const item = pickItem(catalogue, premiumRarityFor(tier), tier, 7);
    return {
      free: null,
      premium: item === null ? null : { kind: 'item', itemId: item.id, paint: proTierPaint(tier) },
    };
  }

  return { free: freeReward(tier, catalogue), premium: premiumReward(tier, catalogue) };
};

const freeReward = (tier: number, catalogue: readonly ItemDef[]): PassReward | null => {
  if (tier % 5 !== 0) return null;
  const step = tier / 5;
  if (step % 5 === 0) return { kind: 'credits', amount: FREE_CREDIT_STEP };
  if (step % 2 === 0) {
    const item = pickItem(catalogue, freeRarityFor(tier), tier, 11);
    return item === null ? { kind: 'xp', amount: FREE_XP_STEP } : { kind: 'item', itemId: item.id };
  }
  return { kind: 'xp', amount: FREE_XP_STEP };
};

const premiumReward = (tier: number, catalogue: readonly ItemDef[]): PassReward | null => {
  if (tier % 10 === 0) return { kind: 'credits', amount: PREMIUM_CREDIT_STEP };
  const item = pickItem(catalogue, premiumRarityFor(tier), tier, 3);
  if (item === null) return { kind: 'xp', amount: FREE_XP_STEP };
  if (tier % 5 === 0 && item.paintable) {
    return { kind: 'item', itemId: item.id, paint: proTierPaint(tier + PASS_TIERS) };
  }
  return { kind: 'item', itemId: item.id };
};

// ---------------------------------------------------------------------------
// Tier points
// ---------------------------------------------------------------------------

export interface TierGain {
  /** Tier Points added. */
  points: number;
  /** Tiers crossed by them. Zero is the common case. */
  tiers: number;
}

/**
 * Add Tier Points and report what they moved.
 *
 * A thin wrapper on ./profile's `applyTierPoints`, which is the single writer
 * of `passTier`; this only exists so a caller can say how far the pass actually
 * advanced without diffing the tier by hand at four call sites.
 */
export const addTierPoints = (progress: PlayerProgress, points: number): TierGain => {
  const clean = Number.isFinite(points) ? Math.max(0, Math.floor(points)) : 0;
  const before = progress.passTier;
  applyTierPoints(progress, clean);
  return { points: clean, tiers: progress.passTier - before };
};

/**
 * Add XP, re-level, and convert the levels gained into Tier Points.
 *
 * The `progress.level` recompute is what stops a pass or challenge XP reward
 * leaving the level stale until the next match ends. The curve itself is
 * ./profile's `levelForXp` — this never decides when a level happens, only
 * what a level is worth to the pass.
 */
export interface XpGain extends TierGain {
  levels: number;
}

export const grantXp = (progress: PlayerProgress, amount: number): XpGain => {
  const clean = Number.isFinite(amount) ? Math.max(0, Math.floor(amount)) : 0;
  const before = levelForXp(progress.xp);
  progress.xp = Math.max(0, progress.xp) + clean;
  const after = levelForXp(progress.xp);
  progress.level = after;

  const levels = Math.max(0, after - before);
  const gain = addTierPoints(progress, levels * TIER_POINTS_PER_LEVEL);
  return { levels, points: gain.points, tiers: gain.tiers };
};

/** A completed challenge's Tier Points, by difficulty. */
export const challengeTierPoints = (difficulty: ChallengeDifficulty): number =>
  CHALLENGE_TIER_POINTS[difficulty];

/** 0..1 through the current tier, for a progress bar. */
export const tierFraction = (progress: PlayerProgress): number =>
  Math.min(1, Math.max(0, progress.tierPoints / TIER_POINTS_PER_TIER));

// ---------------------------------------------------------------------------
// Premium
// ---------------------------------------------------------------------------

export type PremiumFailure = 'already-premium' | 'insufficient-credits';

export interface PremiumResult {
  ok: boolean;
  reason?: PremiumFailure;
  spent: number;
  profile: PlayerProfile;
}

/**
 * Unlock the premium track with earned Credits.
 *
 * The ONLY way it can be unlocked. See the file header: there is no payment
 * path anywhere in this codebase and this function must never grow one.
 */
export const unlockPremium = (profile: PlayerProfile): PremiumResult => {
  if (profile.progress.passPremium) {
    return { ok: false, reason: 'already-premium', spent: 0, profile };
  }
  if (profile.wallet.credits < PASS_PREMIUM_COST) {
    return { ok: false, reason: 'insufficient-credits', spent: 0, profile };
  }
  const next: PlayerProfile = {
    ...profile,
    wallet: { ...profile.wallet, credits: profile.wallet.credits - PASS_PREMIUM_COST },
    progress: { ...profile.progress, passPremium: true },
  };
  return { ok: true, spent: PASS_PREMIUM_COST, profile: next };
};

// ---------------------------------------------------------------------------
// Claims
// ---------------------------------------------------------------------------

export const PASS_CLAIMS_VERSION = 1;
export const PASS_CLAIMS_STORAGE_KEY = 'vroomball.pass';

/**
 * Which tiers have been claimed, per track.
 *
 * A sidecar to `PlayerProfile` — see the file header for why it is not a field
 * on it. Sorted arrays rather than Sets so it round-trips through JSON without
 * a custom serializer.
 */
export interface PassClaims {
  version: number;
  free: number[];
  premium: number[];
}

export const emptyPassClaims = (): PassClaims => ({
  version: PASS_CLAIMS_VERSION,
  free: [],
  premium: [],
});

export const clonePassClaims = (claims: PassClaims): PassClaims => ({
  version: claims.version,
  free: [...claims.free],
  premium: [...claims.premium],
});

/**
 * Repair anything at all into a valid ledger.
 *
 * Total, for the same reason `sanitizeProfile` is: this comes out of
 * localStorage, which is trivially writable from devtools and can hold a
 * half-finished write from a crashed tab. A bad ledger costs the player their
 * claim history and must never stop the pass screen rendering.
 */
export const sanitizePassClaims = (raw: unknown): PassClaims => {
  const source = (raw ?? {}) as Partial<Record<keyof PassClaims, unknown>>;
  const tiers = (value: unknown): number[] => {
    if (!Array.isArray(value)) return [];
    const out = value.filter(
      (n): n is number => typeof n === 'number' && Number.isInteger(n) && n >= 1
    );
    return [...new Set(out)].sort((a, b) => a - b);
  };
  return {
    version: PASS_CLAIMS_VERSION,
    free: tiers(source.free),
    premium: tiers(source.premium),
  };
};

/** Never throws, whatever storage does or whatever is stored. */
export const loadPassClaims = (
  storage: ProfileStorage | null = browserStorage()
): PassClaims => {
  if (!storage) return emptyPassClaims();
  try {
    const raw = readMigrating(storage, PASS_CLAIMS_STORAGE_KEY, legacyKeyFor(PASS_CLAIMS_STORAGE_KEY));
    return raw === null ? emptyPassClaims() : sanitizePassClaims(JSON.parse(raw));
  } catch {
    return emptyPassClaims();
  }
};

/** True if the write landed. A failed save is never worth an exception. */
export const savePassClaims = (
  claims: PassClaims,
  storage: ProfileStorage | null = browserStorage()
): boolean => {
  if (!storage) return false;
  try {
    storage.setItem(PASS_CLAIMS_STORAGE_KEY, JSON.stringify(sanitizePassClaims(claims)));
    return true;
  } catch {
    return false;
  }
};

export const isTierClaimed = (claims: PassClaims, tier: number, track: PassTrack): boolean =>
  (track === 'free' ? claims.free : claims.premium).includes(tier);

export type ClaimFailure =
  /** The tier has not been reached yet. */
  | 'locked'
  | 'already-claimed'
  /** The premium track, without the premium track. */
  | 'not-premium'
  /** This track has nothing at this tier — four free tiers in five. */
  | 'no-reward';

export interface TierClaimResult {
  ok: boolean;
  reason?: ClaimFailure;
  reward: PassReward | null;
  profile: PlayerProfile;
  claims: PassClaims;
}

/**
 * Why a tier cannot be claimed right now, or null. Drives the row's state on
 * the pass screen, and is the same test `claimTier` makes.
 */
export const tierClaimProblem = (
  profile: PlayerProfile,
  claims: PassClaims,
  tier: number,
  track: PassTrack,
  catalogue: readonly ItemDef[] = ITEMS
): ClaimFailure | null => {
  if (tier < 1 || tier > profile.progress.passTier) return 'locked';
  if (tierRewards(tier, catalogue)[track] === null) return 'no-reward';
  if (track === 'premium' && !profile.progress.passPremium) return 'not-premium';
  if (isTierClaimed(claims, tier, track)) return 'already-claimed';
  return null;
};

/**
 * Claim one tier on one track.
 *
 * The order of the checks is the order a player would ask the questions in:
 * have I got there, is there anything here, am I allowed it, and have I already
 * taken it. Only the last is bookkeeping; the first three are all things the
 * row shows before it is pressed.
 */
export const claimTier = (
  profile: PlayerProfile,
  claims: PassClaims,
  tier: number,
  track: PassTrack,
  catalogue: readonly ItemDef[] = ITEMS
): TierClaimResult => {
  const problem = tierClaimProblem(profile, claims, tier, track, catalogue);
  if (problem !== null) return { ok: false, reason: problem, reward: null, profile, claims };

  const reward = tierRewards(tier, catalogue)[track];
  if (reward === null) return { ok: false, reason: 'no-reward', reward: null, profile, claims };

  const next = applyPassReward(profile, reward);
  const ledger = clonePassClaims(claims);
  const list = track === 'free' ? ledger.free : ledger.premium;
  list.push(tier);
  list.sort((a, b) => a - b);
  return { ok: true, reward, profile: next, claims: ledger };
};

/** Hand a pass reward to the player, returning the profile it produced. */
export const applyPassReward = (
  profile: PlayerProfile,
  reward: PassReward
): PlayerProfile => {
  switch (reward.kind) {
    case 'credits':
      return {
        ...profile,
        wallet: { ...profile.wallet, credits: profile.wallet.credits + reward.amount },
      };
    case 'xp': {
      const next: PlayerProfile = { ...profile, progress: { ...profile.progress } };
      grantXp(next.progress, reward.amount);
      return next;
    }
    case 'item':
      return grantItem(
        profile,
        reward.itemId,
        reward.paint === undefined ? {} : { paint: reward.paint }
      );
  }
};

/** Every tier at or below the current one with something unclaimed on it. */
export const claimableTiers = (
  profile: PlayerProfile,
  claims: PassClaims,
  catalogue: readonly ItemDef[] = ITEMS
): { tier: number; track: PassTrack }[] => {
  const out: { tier: number; track: PassTrack }[] = [];
  for (let tier = 1; tier <= profile.progress.passTier; tier++) {
    const rewards = tierRewards(tier, catalogue);
    if (rewards.free !== null && !isTierClaimed(claims, tier, 'free')) {
      out.push({ tier, track: 'free' });
    }
    if (
      profile.progress.passPremium &&
      rewards.premium !== null &&
      !isTierClaimed(claims, tier, 'premium')
    ) {
      out.push({ tier, track: 'premium' });
    }
  }
  return out;
};

export interface ClaimAllResult {
  claimed: number;
  profile: PlayerProfile;
  claims: PassClaims;
}

/**
 * Claim everything claimable, lowest tier first.
 *
 * The list is re-read each pass because an XP reward can level the player up,
 * which advances the pass, which opens tiers that were not claimable when the
 * loop started. Bounded by `LIMIT`, so a reward table that somehow fed itself
 * cannot hang the UI thread.
 */
export const claimAll = (
  profile: PlayerProfile,
  claims: PassClaims,
  catalogue: readonly ItemDef[] = ITEMS
): ClaimAllResult => {
  const LIMIT = 16;
  let current = profile;
  let ledger = claims;
  let claimed = 0;

  for (let round = 0; round < LIMIT; round++) {
    const pending = claimableTiers(current, ledger, catalogue);
    if (pending.length === 0) break;
    for (const { tier, track } of pending) {
      const result = claimTier(current, ledger, tier, track, catalogue);
      if (!result.ok) continue;
      current = result.profile;
      ledger = result.claims;
      claimed++;
    }
  }
  return { claimed, profile: current, claims: ledger };
};
